import type { EFile, EFileStatus } from '@tabservice/tabfile-client';
import { filingStatusMeta } from './status';
import { formatCount } from '@/lib/format';
import { ALL_VALUE, Combobox, type ComboboxOption } from '@/components/ui/combobox';

/**
 * Every status, in the order an e-file passes through them: waiting, on its way, answered.
 *
 * All eight, because these chips are now the only way to narrow the table by status - the
 * select that used to sit beside it held the same list one at a time behind a click. Only the
 * ones the year actually contains are drawn, so the strip stays short without hiding anything
 * a customer has.
 */
const CHIP_ORDER: EFileStatus[] = [
  'queued',
  'transmitting',
  'processing',
  'accepted',
  'accepted_with_errors',
  'partially_accepted',
  'rejected',
  'failed',
];

/** Short enough to sit in a chip. Everything else uses the full status label. */
const SHORT_LABEL: Partial<Record<EFileStatus, string>> = {
  accepted_with_errors: 'With errors',
  partially_accepted: 'Partly accepted',
};

export function chipLabel(status: EFileStatus): string {
  return SHORT_LABEL[status] ?? filingStatusMeta[status].label;
}

export function countByStatus(items: EFile[]): Record<string, number> {
  const counts: Record<string, number> = {};
  for (const filing of items) counts[filing.status] = (counts[filing.status] ?? 0) + 1;
  return counts;
}

/**
 * The status filter, with how many e-files each status holds.
 *
 * It was a row of pills. The portal has no pills anywhere - every filter there is a combo
 * box - and a row that grows with the number of statuses in the year wraps onto a second
 * line on a narrow window, moving the table under it. One control, the counts kept beside
 * each option where they were on the pills.
 */
export function StatusFilter({
  counts,
  total,
  active,
  onSelect,
  className,
}: {
  /** E-files in the year by status, from Tab Service, ignoring the status filter. */
  counts: Record<string, number>;
  /** Every e-file in the year. */
  total: number;
  active: EFileStatus | undefined;
  onSelect: (status: EFileStatus | undefined) => void;
  className?: string;
}) {
  const shown = CHIP_ORDER.filter((status) => (counts[status] ?? 0) > 0 || status === active);

  const options: ComboboxOption[] = [
    { value: ALL_VALUE, label: 'All statuses', hint: formatCount(total) },
    ...shown.map((status) => ({
      value: status,
      label: chipLabel(status),
      hint: formatCount(counts[status] ?? 0),
    })),
  ];

  return (
    <div data-tour="status-chips" className={className}>
      <Combobox
        ariaLabel="Filter e-files by status"
        value={active ?? ALL_VALUE}
        onValueChange={(value) => onSelect(value === ALL_VALUE ? undefined : (value as EFileStatus))}
        options={options}
        placeholder="All statuses"
        searchPlaceholder="Search statuses…"
      />
    </div>
  );
}
